import { ChangeEvent, useMemo, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import './professional-photo-editor.css';
import {
  ACCEPTED_IMAGE_TYPES,
  CARD_ASPECT,
  CARD_EDITOR_STEPS,
  CARD_OUTPUT_HEIGHT,
  CARD_OUTPUT_WIDTH,
  COVER_EDITOR_STEPS,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGE_SIZE_BYTES,
  PROFILE_ASPECT,
  PROFILE_OUTPUT_HEIGHT,
  PROFILE_OUTPUT_WIDTH,
} from './constants';
import { createImage, getCroppedImageBlob } from './cropImage';
import type { PhotoEditorMode, PhotoEditorStep, ProfessionalPhotoCropperProps, ProfessionalPhotoCropResult } from './types';

type EasyCropMediaSize = {
  width: number;
  height: number;
  naturalWidth?: number;
  naturalHeight?: number;
};

function getCenteredCropPixels(mediaSize: EasyCropMediaSize, aspect: number): Area {
  const naturalWidth = mediaSize.naturalWidth ?? mediaSize.width;
  const naturalHeight = mediaSize.naturalHeight ?? mediaSize.height;
  const imageAspect = naturalWidth / naturalHeight;

  let width: number;
  let height: number;

  if (imageAspect > aspect) {
    height = naturalHeight;
    width = height * aspect;
  } else {
    width = naturalWidth;
    height = width / aspect;
  }

  return {
    x: Math.max(0, (naturalWidth - width) / 2),
    y: Math.max(0, (naturalHeight - height) / 2),
    width,
    height,
  };
}

async function getCenteredCropPixelsFromUrl(imageUrl: string, aspect: number): Promise<Area> {
  const image = await createImage(imageUrl);
  return getCenteredCropPixels(
    {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      naturalWidth: image.naturalWidth || image.width,
      naturalHeight: image.naturalHeight || image.height,
    },
    aspect,
  );
}

function getStepNumber(mode: PhotoEditorMode, step: PhotoEditorStep) {
  const steps = mode === 'cover' ? COVER_EDITOR_STEPS : CARD_EDITOR_STEPS;
  const index = steps.findIndex((item) => item.id === step);
  return `${index + 1} de ${steps.length}`;
}

export function ProfessionalPhotoCropper({ isOpen, mode, onClose, onSave }: ProfessionalPhotoCropperProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const createdUrlsRef = useRef<Set<string>>(new Set());

  const [step, setStep] = useState<PhotoEditorStep>('upload');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [profileCrop, setProfileCrop] = useState({ x: 0, y: 0 });
  const [profileZoom, setProfileZoom] = useState(1);
  const [profileCroppedPixels, setProfileCroppedPixels] = useState<Area | null>(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string | null>(null);

  const [cardFile, setCardFile] = useState<File | null>(null);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardCrop, setCardCrop] = useState({ x: 0, y: 0 });
  const [cardZoom, setCardZoom] = useState(1);
  const [cardCroppedPixels, setCardCroppedPixels] = useState<Area | null>(null);
  const [cardPreviewUrl, setCardPreviewUrl] = useState<string | null>(null);

  const acceptedAttr = useMemo(() => ACCEPTED_IMAGE_TYPES.join(','), []);

  if (!isOpen) return null;

  function createEditorUrl(source: Blob | File) {
    const url = URL.createObjectURL(source);
    createdUrlsRef.current.add(url);
    return url;
  }

  function releaseEditorUrl(url: string | null | undefined) {
    if (!url || !createdUrlsRef.current.has(url)) return;
    URL.revokeObjectURL(url);
    createdUrlsRef.current.delete(url);
  }

  function releaseAllEditorUrls() {
    createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    createdUrlsRef.current.clear();
  }

  function resetFlow() {
    releaseAllEditorUrls();
    setStep('upload');
    setError(null);
    setIsSaving(false);
    setProfileFile(null);
    setProfileImageUrl(null);
    setProfileCrop({ x: 0, y: 0 });
    setProfileZoom(1);
    setProfileCroppedPixels(null);
    setProfilePreviewUrl(null);
    setCardFile(null);
    setCardImageUrl(null);
    setCardCrop({ x: 0, y: 0 });
    setCardZoom(1);
    setCardCroppedPixels(null);
    setCardPreviewUrl(null);
  }

  function closeModal() {
    resetFlow();
    onClose();
  }

  function validateFile(file: File) {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'El archivo debe ser JPG, PNG o WEBP.';
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      return `El archivo debe pesar hasta ${MAX_IMAGE_SIZE_MB} MB.`;
    }
    return null;
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      event.target.value = '';
      return;
    }

    releaseAllEditorUrls();
    setError(null);

    if (mode === 'cover') {
      setProfileFile(file);
      setProfileImageUrl(createEditorUrl(file));
      setProfileCrop({ x: 0, y: 0 });
      setProfileZoom(1);
      setProfileCroppedPixels(null);
      setProfilePreviewUrl(null);
      setCardFile(null);
      setCardImageUrl(null);
      setCardCroppedPixels(null);
      setCardPreviewUrl(null);
      setStep('profile');
    } else {
      setCardFile(file);
      setCardImageUrl(createEditorUrl(file));
      setCardCrop({ x: 0, y: 0 });
      setCardZoom(1);
      setCardCroppedPixels(null);
      setCardPreviewUrl(null);
      setProfileFile(null);
      setProfileImageUrl(null);
      setProfileCroppedPixels(null);
      setProfilePreviewUrl(null);
      setStep('card');
    }

    event.target.value = '';
  }

  async function buildProfilePreview() {
    if (!profileImageUrl) {
      setError('Falta cargar una imagen para la portada.');
      return;
    }

    try {
      const crop = profileCroppedPixels ?? (await getCenteredCropPixelsFromUrl(profileImageUrl, PROFILE_ASPECT));
      setProfileCroppedPixels(crop);

      const blob = await getCroppedImageBlob(
        profileImageUrl,
        crop,
        PROFILE_OUTPUT_WIDTH,
        PROFILE_OUTPUT_HEIGHT,
      );
      releaseEditorUrl(profilePreviewUrl);
      setProfilePreviewUrl(createEditorUrl(blob));
      setError(null);
      setStep('review');
    } catch (previewError) {
      console.error('[ProfessionalPhotoCropper] profile preview failed', previewError);
      setError('No se pudo generar la vista previa de portada.');
    }
  }

  async function buildCardPreview() {
    if (!cardImageUrl) {
      setError('Falta una foto para generar la tarjeta.');
      return;
    }

    try {
      const crop = cardCroppedPixels ?? (await getCenteredCropPixelsFromUrl(cardImageUrl, CARD_ASPECT));
      setCardCroppedPixels(crop);

      const blob = await getCroppedImageBlob(
        cardImageUrl,
        crop,
        CARD_OUTPUT_WIDTH,
        CARD_OUTPUT_HEIGHT,
      );
      releaseEditorUrl(cardPreviewUrl);
      setCardPreviewUrl(createEditorUrl(blob));
      setError(null);
      setStep('review');
    } catch (previewError) {
      console.error('[ProfessionalPhotoCropper] card preview failed', previewError);
      setError('No se pudo generar la vista previa de la foto.');
    }
  }

  async function handleFinalSave() {
    if (mode === 'cover' && (!profileFile || !profileImageUrl || !profileCroppedPixels)) {
      setError('Faltan datos para guardar la portada.');
      return;
    }

    if (mode === 'card' && (!cardFile || !cardImageUrl || !cardCroppedPixels)) {
      setError('Faltan datos para guardar la foto de perfil.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let result: ProfessionalPhotoCropResult;

      if (mode === 'cover') {
        const profileBlob = await getCroppedImageBlob(
          profileImageUrl!,
          profileCroppedPixels!,
          PROFILE_OUTPUT_WIDTH,
          PROFILE_OUTPUT_HEIGHT,
        );
        const profileUrl = URL.createObjectURL(profileBlob);

        result = {
          mode,
          profileOriginalFile: profileFile ?? undefined,
          profileCrop: profileCroppedPixels ?? undefined,
          profileBlob,
          profileUrl,
        };
      } else {
        const cardBlob = await getCroppedImageBlob(
          cardImageUrl!,
          cardCroppedPixels!,
          CARD_OUTPUT_WIDTH,
          CARD_OUTPUT_HEIGHT,
        );
        const cardUrl = URL.createObjectURL(cardBlob);

        result = {
          mode,
          cardOriginalFile: cardFile ?? undefined,
          cardCrop: cardCroppedPixels ?? undefined,
          cardBlob,
          cardUrl,
        };
      }

      await onSave(result);
      resetFlow();
      onClose();
    } catch {
      setError('No se pudo guardar el resultado final.');
    } finally {
      setIsSaving(false);
    }
  }

  function renderStepContent() {
    if (step === 'upload') {
      const isCoverMode = mode === 'cover';

      return (
        <div className="bw-photo-modal__upload-panel">
          <div>
            <p className="bw-photo-modal__eyebrow">Paso {getStepNumber(mode, step)}</p>
            <h3>{isCoverMode ? 'Cargar imagen de portada' : 'Cargar foto de perfil'}</h3>
            <span>
              {isCoverMode
                ? `Subí una imagen horizontal, clara y liviana. Formatos JPG, PNG o WEBP. Tamaño máximo: ${MAX_IMAGE_SIZE_MB} MB. Recomendación: 1200 × 500 px. Esta imagen se usará solo como portada de tu perfil público.`
                : `Subí una foto clara, preferentemente vertical. Formatos JPG, PNG o WEBP. Tamaño máximo: ${MAX_IMAGE_SIZE_MB} MB. Recomendación: 320 × 400 px o proporción 4:5. Esta imagen se usará en tu tarjeta de presentación y en los listados de profesionales.`}
            </span>
          </div>
          <button type="button" className="bw-btn bw-btn--primary" onClick={() => fileInputRef.current?.click()}>
            {isCoverMode ? 'Seleccionar imagen' : 'Seleccionar foto'}
          </button>
        </div>
      );
    }

    if (step === 'profile') {
      return (
        <>
          <header className="bw-photo-modal__step-copy">
            <p>Paso {getStepNumber(mode, step)}</p>
            <h3>Ajustar portada del perfil</h3>
            <span>Mové la imagen y ajustá el zoom hasta encontrar el encuadre que querés mostrar.</span>
          </header>

          <div className="bw-cropper-shell bw-cropper-shell--profile">
            {profileImageUrl ? (
              <Cropper
                image={profileImageUrl}
                crop={profileCrop}
                zoom={profileZoom}
                minZoom={1}
                maxZoom={4}
                aspect={PROFILE_ASPECT}
                objectFit="cover"
                restrictPosition
                showGrid={false}
                onCropChange={setProfileCrop}
                onZoomChange={setProfileZoom}
                onMediaLoaded={(mediaSize) => setProfileCroppedPixels(getCenteredCropPixels(mediaSize, PROFILE_ASPECT))}
                onCropComplete={(_, croppedAreaPixels) => setProfileCroppedPixels(croppedAreaPixels)}
              />
            ) : null}
          </div>

          <label className="bw-photo-modal__slider">
            <span>Zoom</span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={profileZoom}
              onChange={(event) => setProfileZoom(Number(event.target.value))}
            />
          </label>
        </>
      );
    }

    if (step === 'card') {
      return (
        <>
          <header className="bw-photo-modal__step-copy bw-photo-modal__step-copy--compact">
            <p>Paso {getStepNumber(mode, step)}</p>
            <h3>Ajustar foto para tarjeta</h3>
            <span>Mové la imagen y ajustá el zoom hasta encontrar el encuadre que querés mostrar.</span>
          </header>

          <div className="bw-card-crop-layout bw-card-crop-layout--card">
            <aside className="bw-card-crop-note" aria-label="Alcance de la foto de perfil">
              <strong>Foto de perfil</strong>
              <p>Se usa en la tarjeta y en los listados profesionales.</p>
            </aside>

            <div className="bw-cropper-shell bw-cropper-shell--card">
              {cardImageUrl ? (
                <Cropper
                  image={cardImageUrl}
                  crop={cardCrop}
                  zoom={cardZoom}
                  minZoom={1}
                  maxZoom={4}
                  aspect={CARD_ASPECT}
                  objectFit="cover"
                  restrictPosition
                  showGrid={false}
                  onCropChange={setCardCrop}
                  onZoomChange={setCardZoom}
                  onMediaLoaded={(mediaSize) => setCardCroppedPixels(getCenteredCropPixels(mediaSize, CARD_ASPECT))}
                  onCropComplete={(_, croppedAreaPixels) => setCardCroppedPixels(croppedAreaPixels)}
                />
              ) : null}
            </div>
          </div>

          <label className="bw-photo-modal__slider bw-photo-modal__slider--card">
            <span>Zoom</span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={cardZoom}
              onChange={(event) => setCardZoom(Number(event.target.value))}
            />
          </label>
        </>
      );
    }

    if (mode === 'card') {
      return (
        <>
          <header className="bw-photo-modal__step-copy bw-photo-modal__step-copy--compact">
            <p>Paso {getStepNumber(mode, step)}</p>
            <h3>Revisar foto de perfil</h3>
            <span>Revisá el encuadre antes de guardar los cambios.</span>
          </header>

          <div className="bw-photo-modal__review-grid bw-photo-modal__review-grid--card-only">
            <article>
              <h4>Foto de perfil / tarjeta</h4>
              <div className="bw-review-card">
                {cardPreviewUrl ? <img src={cardPreviewUrl} alt="Vista previa de tarjeta" /> : null}
              </div>
            </article>
          </div>
        </>
      );
    }

    return (
      <>
        <header className="bw-photo-modal__step-copy bw-photo-modal__step-copy--compact">
          <p>Paso {getStepNumber(mode, step)}</p>
          <h3>Revisar portada</h3>
          <span>Revisá la portada antes de guardar los cambios.</span>
        </header>

        <div className="bw-photo-modal__review-grid bw-photo-modal__review-grid--cover-only">
          <article>
            <h4>Portada del perfil</h4>
            <div className="bw-review-profile">
              {profilePreviewUrl ? <img src={profilePreviewUrl} alt="Vista previa de portada" /> : null}
            </div>
          </article>
        </div>
      </>
    );
  }

  function renderFooter() {
    if (step === 'upload') return null;

    if (step === 'profile') {
      return (
        <>
          <button type="button" className="bw-btn bw-btn--secondary" onClick={() => setStep('upload')}>
            Volver
          </button>
          <button type="button" className="bw-btn bw-btn--primary" onClick={buildProfilePreview}>
            Continuar
          </button>
        </>
      );
    }

    if (step === 'card') {
      return (
        <>
          <button type="button" className="bw-btn bw-btn--secondary" onClick={() => setStep('upload')}>
            Volver
          </button>
          <button type="button" className="bw-btn bw-btn--secondary" onClick={() => fileInputRef.current?.click()}>
            Elegir otra foto
          </button>
          <button type="button" className="bw-btn bw-btn--primary" onClick={buildCardPreview}>
            Continuar
          </button>
        </>
      );
    }

    return (
      <>
        <button type="button" className="bw-btn bw-btn--secondary" onClick={() => setStep(mode === 'cover' ? 'profile' : 'card')}>
          Volver a editar
        </button>
        <button type="button" className="bw-btn bw-btn--primary" onClick={handleFinalSave} disabled={isSaving}>
          {isSaving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </>
    );
  }

  const footer = renderFooter();
  const dialogLabel = mode === 'cover' ? 'Carga y recorte de portada' : 'Carga y recorte de foto de perfil';

  return (
    <div className="bw-photo-modal" role="dialog" aria-modal="true" aria-label={dialogLabel}>
      <div className="bw-photo-modal__backdrop" onClick={closeModal} />
      <div className={`bw-photo-modal__panel bw-photo-modal__panel--${mode}`}>
        <button type="button" className="bw-photo-modal__close" onClick={closeModal}>
          Cerrar
        </button>

        <input ref={fileInputRef} type="file" accept={acceptedAttr} onChange={handleFileChange} hidden />

        <div className={`bw-photo-modal__body bw-photo-modal__body--${step} bw-photo-modal__body--mode-${mode}`}>
          {renderStepContent()}
          {error ? <p className="bw-photo-modal__error">{error}</p> : null}
        </div>

        {footer ? <footer className={`bw-photo-modal__footer bw-photo-modal__footer--${step}`}>{footer}</footer> : null}
      </div>
    </div>
  );
}
